import React from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Tooltip, Typography, Box,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Fenêtre modale affichée en cas d'erreur lors de la phase de jointure / traitement.
 * Affiche le message d'erreur + la stack trace, avec un bouton copier.
 *
 * @param {{ error: { message: string, stack: string } | null, onClose: () => void }} props
 */
export default function ProcessingErrorDialog({ error, onClose }) {
    const [copied, setCopied] = React.useState(false);

    const fullText = error
        ? `${error.message}\n\n${error.stack}`
        : '';

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Dialog
            open={Boolean(error)}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        border: '1.5px solid rgba(211,47,47,0.25)',
                        boxShadow: '0 12px 48px rgba(211,47,47,0.15)',
                    },
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                pb: 1, borderBottom: '1px solid rgba(211,47,47,0.12)',
                bgcolor: 'rgba(211,47,47,0.04)',
            }}>
                <BugReportIcon sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'error.dark' }}>
                        Erreur lors du traitement des données
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Une exception s'est produite pendant la phase de jointure / agrégation.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, pb: 1 }}>
                {/* ── Message principal ── */}
                <Box sx={{
                    px: 1.5, py: 1, mb: 1.5, borderRadius: 2,
                    bgcolor: 'rgba(211,47,47,0.07)',
                    border: '1px solid rgba(211,47,47,0.18)',
                }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'error.dark', fontWeight: 600, wordBreak: 'break-all' }}>
                        {error?.message}
                    </Typography>
                </Box>

                {/* ── Stack trace ── */}
                {error?.stack && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                Stack trace
                            </Typography>
                            <Tooltip title={copied ? 'Copié !' : 'Copier le message + stack trace'}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color={copied ? 'success' : 'inherit'}
                                    startIcon={copied
                                        ? <CheckIcon sx={{ fontSize: '0.85rem !important' }} />
                                        : <ContentCopyIcon sx={{ fontSize: '0.85rem !important' }} />
                                    }
                                    onClick={handleCopy}
                                    sx={{
                                        fontSize: '0.7rem', textTransform: 'none', height: 24, px: 1.2,
                                        borderColor: copied ? 'success.main' : 'rgba(0,0,0,0.2)',
                                        color: copied ? 'success.main' : 'text.secondary',
                                        '&:hover': { borderColor: copied ? 'success.main' : 'primary.main' },
                                    }}
                                >
                                    {copied ? 'Copié' : 'Copier'}
                                </Button>
                            </Tooltip>
                        </Box>
                        <Box
                            component="pre"
                            sx={{
                                m: 0, p: 1.5,
                                borderRadius: 2,
                                bgcolor: '#1e1e2e',
                                color: '#cdd6f4',
                                fontFamily: 'monospace',
                                fontSize: '0.72rem',
                                lineHeight: 1.65,
                                overflowX: 'auto',
                                overflowY: 'auto',
                                maxHeight: '42vh',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                border: '1px solid rgba(255,255,255,0.06)',
                                '&::-webkit-scrollbar': { width: 6, height: 6 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 },
                            }}
                        >
                            {error.stack}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Button onClick={onClose} variant="contained" color="error" size="small" sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

